import { createConfig, http, type CreateConnectorFn } from 'wagmi'
import { bsc, bscTestnet } from 'wagmi/chains'
import { injected, walletConnect } from 'wagmi/connectors'

/**
 * Wallet configuration for the operator panel.
 *
 * Deliberately thinner than the public app's. The panel never signs a transaction —
 * the only thing a wallet does here is produce ONE SIWE signature to prove the
 * operator controls an address listed in the API's ADMIN_ADDRESSES. There is nothing
 * to switch chains for and no contract to read, so the chain list is only what the
 * EIP-4361 message needs to describe honestly.
 *
 * Coinbase Wallet is omitted for the same reason it would be pointless here: its SDK
 * is a large dependency whose value is smart-wallet transaction support, and an
 * operator signing in from a desktop browser has an injected wallet.
 */

const WC_PROJECT_ID = import.meta.env.VITE_WALLETCONNECT_PROJECT_ID?.trim()

/** True when WalletConnect is usable. The picker reads this to explain why it's missing. */
export const walletConnectEnabled = Boolean(WC_PROJECT_ID)

/**
 * The chain the SIWE message names when the wallet reports none.
 *
 * Only a fallback — `useAuth` prefers the chain the wallet is ACTUALLY on, because
 * the message is a signed statement about where the signer is and a hardcoded value
 * would make it a false one.
 */
export const fallbackChainId = Number(import.meta.env.VITE_CHAIN_ID ?? bscTestnet.id)

function buildConnectors(): CreateConnectorFn[] {
  const connectors: CreateConnectorFn[] = [injected({ shimDisconnect: true })]

  // Registering walletConnect without a project ID throws at config construction and
  // takes the whole panel down — so an unconfigured deployment falls back to injected
  // rather than showing a white screen.
  if (WC_PROJECT_ID) {
    connectors.push(
      walletConnect({
        projectId: WC_PROJECT_ID,
        metadata: {
          name: 'CrystalDEX Operations',
          description: 'Operator console for the CrystalDEX protocol',
          url: typeof window !== 'undefined' ? window.location.origin : 'https://crystaldex.com',
          icons: [],
        },
        showQrModal: true,
      }),
    )
  }

  return connectors
}

export const wagmiConfig = createConfig({
  chains: [bscTestnet, bsc],
  connectors: buildConnectors(),
  transports: {
    [bsc.id]: http(),
    // Default BSC-testnet RPCs rate-limit hard; publicnode is reliable.
    [bscTestnet.id]: http('https://bsc-testnet-rpc.publicnode.com'),
  },
  // Client-rendered SPA — no SSR hydration to reconcile.
  ssr: false,
})

/** Display metadata for the connector picker, keyed by wagmi connector type. */
export const CONNECTOR_META: Record<string, { label: string; hint: string }> = {
  injected: {
    label: 'Browser wallet',
    hint: 'MetaMask, Trust, Binance Wallet, OKX',
  },
  walletConnect: {
    label: 'WalletConnect',
    hint: 'Scan a QR code with a mobile wallet',
  },
}

declare module 'wagmi' {
  interface Register {
    config: typeof wagmiConfig
  }
}
